import React, { useState } from 'react';

/**
 * OptimizedImage Component
 * - Serves WebP with PNG/JPG fallback
 * - Lazy loads images below the fold
 * - Adds proper alt text for accessibility
 */
const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  width,
  height,
  lazy = true,
  priority = false, // Set true for above-the-fold images
  ...props 
}) => {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  // Handle uploaded images - use relative URLs so they work on any domain
  const getImageSrc = (imageSrc) => {
    if (!imageSrc) return '';
    
    // NEW: Static menu uploads (persisted in git) - use as-is (served by React)
    if (imageSrc.startsWith('/images/menu-uploads/')) {
      return imageSrc;
    }
    
    // OLD: Backend-served uploads (ephemeral) - add /api prefix if needed
    if (imageSrc.startsWith('/images/uploads/')) {
      return `/api${imageSrc}`;
    }
    if (imageSrc.startsWith('/api/images/uploads/')) {
      return imageSrc;
    }
    
    // For external URLs and other paths, return as-is
    return imageSrc;
  };

  const resolvedSrc = getImageSrc(src);

  // Reset error/retry state whenever the source changes so a new/updated image
  // is never stuck behind a stale "Image unavailable" placeholder.
  React.useEffect(() => {
    setHasError(false);
    setIsLoaded(false);
    setRetryCount(0);
  }, [resolvedSrc]);

  // Determine loading strategy
  const loadingStrategy = priority ? 'eager' : (lazy ? 'lazy' : 'eager');
  const fetchPriority = priority ? 'high' : 'auto';

  // Handle load error: retry a couple of times (with a cache-busting param) to
  // recover from transient network hiccups / slow R2 loads before giving up.
  const handleError = () => {
    if (retryCount < MAX_RETRIES) {
      setRetryCount((c) => c + 1);
      return;
    }
    console.warn('Image failed to load after retries:', resolvedSrc);
    setHasError(true);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  // Cache-busting suffix on retry so the browser re-requests the image.
  const displaySrc = retryCount > 0
    ? `${resolvedSrc}${resolvedSrc.includes('?') ? '&' : '?'}retry=${retryCount}`
    : resolvedSrc;

  // If image failed to load, show placeholder
  if (hasError) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 text-gray-400 text-xs text-center p-2 ${className}`}
        style={{ width, height, minHeight: '100px' }}
      >
        <span>Image unavailable</span>
      </div>
    );
  }
  
  // Simple img tag - no WebP conversion (causes issues with external URLs)
  return (
    <img
      src={displaySrc}
      alt={alt || 'Image'}
      className={className}
      loading={loadingStrategy}
      fetchPriority={fetchPriority}
      width={width}
      height={height}
      decoding={priority ? 'sync' : 'async'}
      onError={handleError}
      onLoad={handleLoad}
      {...props}
    />
  );
};

export default OptimizedImage;
